import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppButton } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import {
  IconCheck,
  IconError,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import { getContractTierInfo, getCustomerDetail, getInstallation } from "../api";
import {
  formatHHMM,
  paymentCacheAgeHours,
  readPaymentCache,
  writePaymentCache,
  type PaymentCacheEntry,
} from "../offline";

/**
 * S4 — Payment check (handoff TASK-008, s4-payment-*).
 * Deposit gate before submission: GO when ledger amount_paid covers the
 * compulsory advance (`initial_deposit` when `advance_required`), else the
 * installment / env min. Offline / ledger error: cached summary (≤ 24 h)
 * with "as of HH:MM"; no cache → distinct ledger-unreachable state with Retry.
 */

function tzs(n: number): string {
  return `TZS ${new Intl.NumberFormat("en-TZ", { maximumFractionDigits: 0 }).format(n)}`;
}

export function S4PaymentCheck() {
  const { id = "" } = useParams();
  const installationId = Number(id);
  const navigate = useNavigate();
  useFieldHeader("Payment check", "Deposit gate before submission");
  useFieldJourney({
    flow: "install",
    step: 3,
    backTo: `/field/installations/${id}/evidence`,
  });

  const instQuery = useQuery({
    queryKey: ["installation", installationId],
    queryFn: () => getInstallation(installationId),
    retry: 1,
    staleTime: 15_000,
  });
  const inst = instQuery.data?.installation;

  const customerQuery = useQuery({
    queryKey: ["inst-customer", inst?.customer_id ?? 0],
    queryFn: () => getCustomerDetail(inst!.customer_id),
    enabled: inst != null,
    retry: 1,
    staleTime: 30_000,
  });
  const contract = customerQuery.data?.customer.active_contract ?? null;

  const contractId = inst?.contract_id ?? contract?.id ?? null;
  const tierQuery = useQuery({
    queryKey: ["inst-tier-info", contractId ?? 0],
    queryFn: () => getContractTierInfo(contractId!),
    enabled: contractId != null,
    retry: 1,
    staleTime: 30_000,
  });

  // Ledger figures: payment-service tier-info wins; fall back to the contract snapshot.
  const amountPaid = tierQuery.data?.status.amount_paid ?? contract?.amount_paid ?? null;
  // Compulsory advance → initial_deposit; otherwise installment as soft target (server uses env min).
  const advanceRequired = contract?.advance_required !== false;
  const depositRequired = (() => {
    if (!contract) return null;
    const deposit = Number(contract.initial_deposit ?? 0);
    if (advanceRequired && deposit > 0) return deposit;
    return contract.expected_payment_amount ?? deposit;
  })();
  const arrears = contract?.arrears ?? 0;
  const tierName = tierQuery.data?.tier.name ?? null;
  const durationMonths = tierQuery.data?.tier.duration_months ?? null;

  const ready =
    inst != null && customerQuery.isSuccess && amountPaid !== null && depositRequired !== null;

  // Cache the verified summary for the offline variant.
  useEffect(() => {
    if (!ready) return;
    writePaymentCache({
      at: new Date().toISOString(),
      installationId,
      contractId,
      contractNumber: contract?.contract_number ?? tierQuery.data?.contract_number ?? null,
      tierName,
      durationMonths,
      depositRequired,
      amountPaid: amountPaid!,
      arrears,
    });
  }, [ready, installationId, contractId, contract, tierQuery.data, tierName, durationMonths, depositRequired, amountPaid, arrears]);

  const cached = useMemo(() => readPaymentCache(installationId), [installationId]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (instQuery.isPending || (inst != null && customerQuery.isPending)) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Checking ledger…</h2>
      </div>
    );
  }

  // ── No active contract → NO-GO (checked before the generic error branch) ──
  if (inst != null && customerQuery.isSuccess && !contract) {
    return (
      <>
        <div className="pg-gono-banner no-go">
          <IconWarning size={24} />
          <div>
            NO-GO — no active contract
            <div className="gono-sub">The customer has no active contract on record.</div>
          </div>
        </div>
        <div className="pg-actions">
          <AppButton
            variant="secondary"
            type="button"
            onClick={() => navigate("/field/installations")}
          >
            Save draft &amp; contact office
          </AppButton>
        </div>
      </>
    );
  }

  // ── Error / offline → cached summary or ledger-unreachable ─────────────────
  if (instQuery.isError || customerQuery.isError || !ready) {
    if (cached) {
      const stale = paymentCacheAgeHours(cached) > 24;
      return (
        <>
          <div className="pg-offline-banner" role="status">
            <IconWarning size={14} /> Cached payment data — as of {formatHHMM(cached.at)}
            {stale ? " (over 24 h old)" : ""}
          </div>
          <PaymentBody
            data={cached}
            stale={stale}
            onContinue={() => navigate(`/field/installations/${installationId}/submit`)}
            onSaveDraft={() => navigate("/field/installations")}
          />
        </>
      );
    }
    const err = instQuery.error ?? customerQuery.error;
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Payment check failed</h2>
        <p>
          Ledger unreachable — the deposit cannot be verified right now.
          <br />
          {err instanceof Error ? err.message : ""}
        </p>
        <AppButton
          variant="secondary"
          type="button"
          onClick={() => {
            void instQuery.refetch();
            void customerQuery.refetch();
            void tierQuery.refetch();
          }}
        >
          Retry
        </AppButton>
      </div>
    );
  }

  // ── Live summary ───────────────────────────────────────────────────────────
  // `ready` guarantees amountPaid / depositRequired / contract are non-null.
  if (!contract) return null;

  return (
    <PaymentBody
      data={{
        at: new Date().toISOString(),
        installationId,
        contractId,
        contractNumber: contract.contract_number,
        tierName,
        durationMonths,
        depositRequired,
        amountPaid: amountPaid ?? 0,
        arrears,
      }}
      stale={false}
      onContinue={() => navigate(`/field/installations/${installationId}/submit`)}
      onSaveDraft={() => navigate("/field/installations")}
    />
  );
}

function PaymentBody({
  data,
  stale,
  onContinue,
  onSaveDraft,
}: {
  data: PaymentCacheEntry;
  stale: boolean;
  onContinue: () => void;
  onSaveDraft: () => void;
}) {
  const go = data.depositRequired !== null && data.amountPaid >= data.depositRequired;
  const shortfall = data.depositRequired !== null ? data.depositRequired - data.amountPaid : 0;

  return (
    <>
      {go ? (
        <div className="pg-gono-banner go">
          <IconCheck size={24} />
          <div>
            GO — deposit verified
            <div className="gono-sub">The required deposit is covered by payments received.</div>
          </div>
        </div>
      ) : (
        <div className="pg-gono-banner no-go">
          <IconWarning size={24} />
          <div>
            NO-GO — {tzs(shortfall)} shortfall
            <div className="gono-sub">
              The deposit is not covered. Save a draft and contact the office.
            </div>
          </div>
        </div>
      )}

      <div className="pg-summary">
        <div className="row">
          <span className="k">Plan tier</span>
          <span className="v">
            {data.tierName
              ? `${data.tierName}${data.durationMonths ? ` · ${data.durationMonths} months` : ""}`
              : (data.contractNumber ?? "—")}
          </span>
        </div>
        <div className="row">
          <span className="k">Deposit required</span>
          <span className="v">{data.depositRequired !== null ? tzs(data.depositRequired) : "—"}</span>
        </div>
        <div className="row">
          <span className="k">Deposit paid</span>
          <span className="v">{tzs(data.amountPaid)}</span>
        </div>
        <div className="row">
          <span className="k">Arrears</span>
          <span className="v" style={data.arrears > 0 ? { color: "var(--pg-error)" } : undefined}>
            {tzs(data.arrears)}
          </span>
        </div>
        <div className="row">
          <span className="k">First token</span>
          <span className="v">{go ? "After office approval" : "Blocked"}</span>
        </div>
      </div>

      <div className="pg-actions">
        {go && !stale ? (
          <AppButton type="button" onClick={onContinue}>
            Continue to submit
          </AppButton>
        ) : (
          <AppButton variant="secondary" type="button" onClick={onSaveDraft}>
            Save draft &amp; contact office
          </AppButton>
        )}
      </div>
    </>
  );
}
